/* eslint-disable react/style-prop-object */
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import React, { memo, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import Box from 'ui-box';
import { useDebouncedCallback } from 'use-debounce/lib';
import {
  majorScale,
  Button,
  EditableText,
  Checkbox,
  Spinner,
  Icon,
  useToast,
  minorScale,
  useAlert,
  defaultFills,
} from '../../npaper';
import styles from './SaleItemsTable.module.css';
import { formatUnit } from '../../npaper/FormattedUnit';

import {
  ISale,
  useStores,
  ISaleItem,
  IAgreement,
  Unit,
  SaleType,
  SaleStatus,
} from '../../stores';
import { ISaleItemSnapshotIn } from '../../stores/SaleItemStore';
import { ContributionMarginText, CostPriceText } from '../products/PriceTexts';
import { ProductSupplierDetailSelect } from '../products/ProductSupplierSelect';
import { ProductThumbnail } from '../products/ProductThumbnail';
import { TaskSideSheet } from '../tasks/TaskSideSheet';
import { SaleItemActionMenu } from './SaleItemActionMenu';
import { SaleItemStatusCell } from './SaleItemStatusCell';

export interface ISaleItemsTableProps {
  sale?: ISale;
  agreement?: IAgreement;
  showTotals?: boolean;
}

export const SaleItemsTable: React.FC<ISaleItemsTableProps> = observer(
  ({ sale, agreement, showTotals = false }) => {
    const { saleItemStore, saleStore, agreementStore } = useStores();
    const toaster = useToast();
    const [items, setItems] = useState<ISaleItem[]>();
    const [addingItemsCount, setAddingItemsCount] = useState(0);
    const [selectedRows, setSelectedRows] = useState<{
      [key: number]: boolean;
    }>({});

    const _items = (sale && getSnapshot(sale.items)) || agreement;

    useEffect(() => {
      let isSub = true;
      if (!sale && !agreement) {
        throw new Error('You must provide either a sale or an agreement!');
      }
      let filter = {
        sale: sale ? sale.id : undefined,
        parent__isnull: true,
        agreement: agreement ? agreement.id : undefined,
        ordering: ['position'],
      };
      saleItemStore.invalidateCaches();
      saleItemStore.getAll({ filter }).then(
        ({ items: lines }) =>
          isSub &&
          // We only want to get items that are not children. This is important! Otherwise we would show some items that are children to others
          setItems(Array.from<ISaleItem>(lines).filter((l) => !l.parent_item))
      );
      return () => {
        isSub = false;
      };
    }, [sale, agreement, saleItemStore, _items]);

    const currency = sale ? sale.currency : agreement!.currency;
    const totalPrice = sale ? sale.sub_total : agreement!.price;
    const discount = sale ? sale.discount : undefined;
    const costPrice = sale ? sale.cost_price : agreement!.cost_price;

    const contributionMargin = sale
      ? sale.contribution_margin
      : agreement!.contribution_margin;

    const contributionMarginRatio = sale
      ? sale.contribution_margin_ratio
      : agreement!.contribution_margin_ratio;

    // Calclulate if we can add/remove items
    const salePermissions = React.useMemo(() => {
      if (agreement)
        return {
          canAddItems: true,
          canChangeQuantity: true,
          canChangeUnitPrice: true,
          canChangePosition: true,
          canHide: true,
        };

      const isOffer = sale && sale.type === SaleType.OFFER;
      const isOrder = sale && sale.type === SaleType.ORDER;
      const isInvoice = sale && sale.type === SaleType.INVOICE;
      const isDraft = sale && sale.status === SaleStatus.DRAFT;
      const isVoid = sale && sale.status === SaleStatus.VOID;

      const canAddItems = isOffer || (isOrder && !isVoid) || false;

      const canChangeQuantity = isOffer || (isOrder && !isVoid) || false;
      const canChangeUnitPrice =
        isOffer || (isOrder && !isVoid) || (isInvoice && isDraft) || false;

      return {
        canAddItems,
        canChangeQuantity,
        canChangeUnitPrice,
        canChangePosition: canChangeUnitPrice,
        canHide: canChangeUnitPrice,
      };
    }, [agreement, sale]);

    // Calculate if we need to hide the status
    const hideStatus =
      (sale && sale.type === SaleType.OFFER) ||
      (sale &&
        sale.type === SaleType.ORDER &&
        sale.status === SaleStatus.DRAFT);

    const deleteLine = async (line: ISaleItem) => {
      console.log(line);
      setItems((l) => {
        const ls = Array.from(l || []);
        ls.splice(ls.indexOf(line), 1);
        return ls;
      });
      try {
        await saleItemStore.deleteItem(line);
        await saleStore.getItem(getSnapshot(line).sale, true);
      } catch (err) {
        await saleStore.getItem(getSnapshot(line).sale, true);
        toaster.showDefaultError(err);
      }
    };

    const refreshSaleOrAgreement = async () => {
      if (sale) await saleStore.getItem(sale.id, true);
      if (agreement) await agreementStore.getItem(agreement.id, true);
    };

    const addLine = async (item: Partial<ISaleItemSnapshotIn>) => {
      setAddingItemsCount((c) => c + 1);
      const line = await saleItemStore.createItem(item);
      refreshSaleOrAgreement();
      setItems((l) => [...(l || []), line]);
      setAddingItemsCount((c) => c - 1);
    };

    const reorder = (result: DropResult) => {
      if (!result.destination || !items) return;
      // Reorder from result.source.index to result.destination.index
      setItems((ls) => {
        const lines = Array.from(ls || []);
        const removed = lines.splice(result.source.index, 1);
        if (removed.length === 0) return ls;
        lines.splice(result.destination!.index, 0, ...removed);
        return lines;
      });
      const item = items[result.source.index];
      saleItemStore.updateItem(item, {
        position: result.destination!.index,
      });
    };

    const gridColumns = hideStatus
      ? '24px minmax(280px, auto) 100px 160px 100px 160px 160px 180px 60px 60px'
      : '24px minmax(280px, auto) 100px 160px 100px 160px 160px 180px 60px 130px 60px';

    const hasSelectedRows =
      Object.values(selectedRows).find((i) => !!i) &&
      salePermissions.canAddItems;

    return !items ? (
      <>
        <Spinner size={16} />
      </>
    ) : (
      <>
        {items.length === 0 && (
          <Box marginBottom={majorScale(2)}>
            <p>
              <FormattedMessage defaultMessage="No lines added yet" />
            </p>
          </Box>
        )}

        {items.length > 0 && (
          <Box
            overflow="auto"
            position="relative"
            marginTop={majorScale(-2)}
            maxHeight="80vh"
          >
            <Box
              background="white"
              display="grid"
              gridAutoFlow="column"
              gridTemplateColumns={gridColumns}
              alignItems="center"
              position="sticky"
              top={0}
              zIndex={3}
            >
              <Box
                className="table-th"
                height="100%"
                position="sticky"
                left={0}
              />
              <Box
                className="table-th"
                backgroundColor="white"
                position="sticky"
                left={24}
              >
                Product
              </Box>
              <Box
                className="table-th"
                backgroundColor="white"
                textAlign="right"
              >
                Quantity
              </Box>
              <Box
                className="table-th"
                backgroundColor="white"
                textAlign="right"
              >
                Unit price
              </Box>
              <Box
                className="table-th"
                backgroundColor="white"
                textAlign="right"
              >
                Discount
              </Box>
              <Box
                className="table-th"
                backgroundColor="white"
                textAlign="right"
              >
                Subtotal
              </Box>
              <Box
                className="table-th"
                backgroundColor="white"
                textAlign="right"
              >
                Cost price
              </Box>
              <Box
                className="table-th"
                backgroundColor="white"
                textAlign="right"
              >
                CM
              </Box>
              <Box
                className="table-th"
                backgroundColor="white"
                textAlign="right"
              >
                <span>
                  <FormattedMessage defaultMessage="Hidden" />
                </span>
              </Box>
              {!hideStatus && (
                <Box
                  className="table-th"
                  backgroundColor="white"
                  textAlign="right"
                >
                  <FormattedMessage defaultMessage="Status" />
                </Box>
              )}

              <Box className="table-th" height="100%" background="white" />
            </Box>

            <DragDropContext
              onDragEnd={(result, provided) => {
                reorder(result);
              }}
            >
              <Droppable droppableId="sale-items-table">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {items.map((line, i) => (
                      <Draggable
                        draggableId={`line-${line.id}`}
                        key={line.id}
                        index={i}
                        isDragDisabled={!salePermissions.canChangePosition}
                      >
                        {(provided, snapshot) => (
                          <SaleItemTableRow
                            gridTemplateColumns={gridColumns}
                            saleItem={line}
                            draggableProps={{ provided, snapshot }}
                            onDeleteLine={() => deleteLine(line)}
                            showStatus={!hideStatus}
                            selected={selectedRows[line.id as number]}
                            onSelect={() => {
                              setSelectedRows((r) => ({
                                ...r,
                                [line.id as number]: !r[line.id as number],
                              }));
                            }}
                            {...salePermissions}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {new Array(addingItemsCount).fill(0).map((a, i) => (
              <Box
                key={i}
                borderBottom={`1px solid ${defaultFills.normal[100]}`}
              >
                <Box
                  className="loading skeleton"
                  marginY={majorScale(1)}
                  height={38}
                  borderRadius={4}
                />
              </Box>
            ))}

            <Box
              background="white"
              display="grid"
              gridAutoFlow="column"
              gridTemplateColumns={gridColumns}
              alignItems="center"
              position="sticky"
              bottom={-1}
              zIndex={3}
              borderTop={`1px solid ${defaultFills.normal[100]}`}
            >
              <Box position="sticky" left={0} />
              <Box />
              <Box />
              <Box />
              <Box
                className="font-mono medium"
                textAlign="right"
                justifyContent="right"
              >
                {discount !== undefined ? (
                  <FormattedNumber
                    format="currency"
                    currency={currency}
                    value={discount}
                  />
                ) : (
                  <Spinner size={16} />
                )}
              </Box>
              <Box
                className="font-mono medium"
                textAlign="right"
                justifyContent="right"
              >
                {totalPrice === undefined ? (
                  <Spinner size={16} />
                ) : (
                  <FormattedNumber
                    format="currency"
                    currency={currency}
                    value={totalPrice}
                  />
                )}
              </Box>
              <Box
                className="font-mono medium"
                textAlign="right"
                justifyContent="right"
              >
                <CostPriceText vertical costPrice={costPrice} />
              </Box>
              <Box
                textAlign="right"
                justifyContent="right"
                paddingY={majorScale(0.5)}
                className={classNames(
                  'font-mono medium',
                  (contributionMarginRatio.precise ||
                    contributionMarginRatio.minimum ||
                    0) <= 0 && 'danger'
                )}
              >
                <ContributionMarginText
                  contributionMargin={contributionMargin}
                  vertical
                  showIndication
                />{' '}
                <span className="muted">
                  ({contributionMarginRatio.precise === null && '>'}
                  <FormattedNumber
                    style="percent"
                    value={
                      contributionMarginRatio.precise ||
                      contributionMarginRatio.minimum ||
                      0
                    }
                  />
                  )
                </span>
              </Box>
              <Box
                width="100%"
                height="100%"
                borderTop={`1px solid ${defaultFills.normal[100]}`}
                background="white"
                marginTop={-2}
              />
              {!hideStatus && (
                <Box
                  width="100%"
                  height="100%"
                  borderTop={`1px solid ${defaultFills.normal[100]}`}
                  background="white"
                  marginTop={-2}
                />
              )}
              <Box
                width="100%"
                height="100%"
                borderTop={`1px solid ${defaultFills.normal[100]}`}
                background="white"
                marginTop={-2}
              />
            </Box>
          </Box>
        )}
        {/* If any rows are selected, we show some actions */}
        {hasSelectedRows && (
          <Box
            display="inline-grid"
            gridAutoFlow="column"
            columnGap={majorScale(1)}
            marginTop={majorScale(1)}
          >
            <Button
              primary
              intent="info"
              onClick={async () => {
                const selected: number[] = Object.keys(selectedRows)
                  .filter((key) => selectedRows[key as any as number])
                  .map((id) => parseInt(id));

                const item = {
                  is_heading: false,
                  sale: sale ? sale.id : undefined,
                  agreement: !sale && agreement ? agreement.id : undefined,
                  child_items: selected,
                };
                setSelectedRows({});
                return addLine(item).then(() =>
                  setItems((items) =>
                    (items || []).filter(
                      (item) => selected.indexOf(item.id) === -1
                    )
                  )
                );
              }}
            >
              <FormattedMessage defaultMessage="Group together" />
            </Button>
            <Button
              primary
              intent="danger"
              onClick={async () => {
                const selected: ISaleItem[] = Object.keys(selectedRows)
                  .filter((key) => selectedRows[key as any as number])
                  .map((id) => items.find((item) => item.id + '' === id + ''))
                  .filter((item) => !!item) as ISaleItem[];
                setSelectedRows({});
                return Promise.all(selected.map(deleteLine));
              }}
            >
              <FormattedMessage defaultMessage="Delete lines" />
            </Button>
          </Box>
        )}

        {!hasSelectedRows && salePermissions.canAddItems && (
          <Box display="flex" marginTop={majorScale(1)}>
            <div>
              <ProductSupplierDetailSelect
                onSelect={async (product) => {
                  const snapshot = getSnapshot(product);
                  const item = {
                    sale: sale ? sale.id : undefined,
                    agreement: !sale && agreement ? agreement.id : undefined,
                    product: snapshot.id,
                  };
                  addLine(item);
                }}
                onSelectSupplierProductItem={async (supplierItem) => {
                  const snapshot = getSnapshot(supplierItem);
                  const item = {
                    sale: sale ? sale.id : undefined,
                    agreement: !sale && agreement ? agreement.id : undefined,
                    supplier_item: snapshot.id,
                  };
                  addLine(item);
                }}
              >
                <Button iconLeft="add">
                  <FormattedMessage defaultMessage="Add product" />
                </Button>
              </ProductSupplierDetailSelect>
            </div>
            <Button
              marginLeft={majorScale(1)}
              iconLeft="add"
              onClick={async () => {
                const item = {
                  sale: sale ? sale.id : undefined,
                  agreement: !sale && agreement ? agreement.id : undefined,
                };
                addLine(item);
              }}
            >
              <FormattedMessage defaultMessage="Add text line" />
            </Button>
            <Button
              iconLeft="add"
              marginLeft={majorScale(1)}
              onClick={async () => {
                const item = {
                  is_heading: true,
                  sale: sale ? sale.id : undefined,
                  agreement: !sale && agreement ? agreement.id : undefined,
                };
                addLine(item);
              }}
            >
              <FormattedMessage defaultMessage="Add heading" />
            </Button>
          </Box>
        )}

        {showTotals && items && items.length > 0 && (
          <Box marginTop={majorScale(4)}>
            <Box
              display="grid"
              gridTemplateColumns="1fr 160px"
              columnGap={majorScale(4)}
              rowGap={majorScale(1)}
              textAlign="right"
              paddingRight={majorScale(5)}
            >
              <p className="medium">
                <FormattedMessage defaultMessage="Sub total" />
              </p>
              <p className="font-mono medium">
                <FormattedNumber
                  format="currency"
                  currency={currency}
                  value={sale ? sale.sub_total : 0}
                />
              </p>
              {sale &&
                sale.vat_amounts.map((vatAmount, i) =>
                  vatAmount.vat_rate ? (
                    <React.Fragment key={vatAmount.vat_rate.id}>
                      <p>
                        {vatAmount.vat_rate.display_name} (
                        <FormattedNumber
                          format="percent"
                          value={vatAmount.vat_rate.rate}
                        />
                        )
                      </p>
                      <p className="font-mono">
                        <FormattedNumber
                          format="currency"
                          currency={currency}
                          value={vatAmount.amount}
                        />
                      </p>
                    </React.Fragment>
                  ) : (
                    <React.Fragment key={'index' + i}>
                      <p></p>
                      <Box textAlign="right">
                        <Spinner size={16} />
                      </Box>
                    </React.Fragment>
                  )
                )}

              <p className="medium">
                <FormattedMessage
                  defaultMessage="Total"
                  description="Total amount (on sale)"
                />
              </p>
              <p className="font-mono medium">
                <FormattedNumber
                  format="currency"
                  currency={currency}
                  value={sale ? sale.total : 0}
                />
              </p>
            </Box>
          </Box>
        )}
      </>
    );
  }
);

const SaleItemTableRow = observer(
  ({
    saleItem,
    draggableProps: { provided, snapshot },
    onDeleteLine,
    gridTemplateColumns,
    showStatus = true,
    canAddItems = true,
    canChangeQuantity = true,
    canChangeUnitPrice = true,
    canChangePosition = true,
    canHide = true,
    onSelect,
    selected,
  }: {
    saleItem: ISaleItem;
    showStatus?: boolean;
    canAddItems?: boolean;
    canChangeQuantity: boolean;
    canChangeUnitPrice: boolean;
    canChangePosition: boolean;
    gridTemplateColumns: string;
    canHide: boolean;
    draggableProps: {
      provided: DraggableProvided;
      snapshot: DraggableStateSnapshot;
    };
    onSelect: () => void;
    selected: boolean;
    onDeleteLine: () => void | Promise<any>;
  }) => {
    const intl = useIntl();
    const toaster = useToast();
    const { saleItemStore, saleStore, agreementStore } = useStores();

    const deleteLineAlert = useAlert({
      title: intl.formatMessage({
        defaultMessage: 'Delete line?',
      }),
      message: intl.formatMessage({
        defaultMessage:
          'Are you sure you want to delete this line? Any purchase order or task attached to the line will not be deleted.',
      }),
      confirmButtonText: intl.formatMessage({
        defaultMessage: 'Delete line',
      }),
      intent: 'danger',
      icon: 'trash',
    });

    const [inspectTask, setInspectTask] = useState(false);

    const updateItem = useDebouncedCallback((data: any) => {
      const snapshot = getSnapshot(saleItem);
      saleItemStore
        .updateItem(saleItem, data)
        .then(() => {
          if (snapshot.sale) saleStore.getItem(snapshot.sale, true);
          if (snapshot.agreement)
            agreementStore.getItem(snapshot.agreement, true);
          if (snapshot.child_items) {
            snapshot.child_items.forEach((child) =>
              saleItemStore.getItemWithQueue(child, true)
            );
          }
        })
        .catch((err) => {
          saleItemStore.getItemWithQueue(snapshot.id, true);
          toaster.showDefaultError(err);
        });
    }, 0);

    return (
      <Box
        {...provided.draggableProps}
        style={{
          ...provided.draggableProps.style,
        }}
        ref={provided.innerRef}
      >
        {saleItem.task && (
          <TaskSideSheet
            isShown={inspectTask}
            onClose={() => setInspectTask(false)}
            task={saleItem.task}
          />
        )}
        <Box
          className={classNames(styles.row, selected && styles.selected)}
          display="grid"
          gridAutoFlow="column"
          alignItems="center"
          gridTemplateColumns={gridTemplateColumns}
          backgroundColor="white"
        >
          <Box
            position="sticky"
            left={0}
            paddingLeft={majorScale(1)}
            zIndex={2}
            onClick={() => onSelect()}
            {...provided.dragHandleProps}
          >
            <Icon icon="drag-handle-vertical" />
          </Box>
          {saleItem.is_heading ? (
            <>
              <Box
                position="sticky"
                left={24}
                padding={majorScale(1)}
                gridColumnStart={2}
                gridColumnEnd={4}
                zIndex={2}
              >
                {canHide ? (
                  <EditableText
                    value={saleItem.title}
                    placeholder="Skriv en overskrift"
                    fontWeight={500}
                    type="text"
                    maxLength={600}
                    onConfirm={(s) => {
                      saleItem.setTitle(s);
                      updateItem.callback({ title: s });
                    }}
                  />
                ) : (
                  <Box is="span" fontWeight={500}>
                    {saleItem.title}
                  </Box>
                )}
              </Box>
              <Box />
              <Box />
              <Box />
              <Box />
              <Box />
              <Box />
              {showStatus && <Box />}

              <Box>
                <SaleItemActionMenu
                  saleItem={saleItem}
                  onDeleteLine={() => {
                    deleteLineAlert.show(async () => onDeleteLine());
                  }}
                  canChangeUnitPrice={canChangeUnitPrice}
                  canAddItems={canAddItems}
                  showStatus={showStatus}
                />
              </Box>
            </>
          ) : (
            <>
              <Box position="sticky" left={24} zIndex={2}>
                <EditableLineTitle
                  saleItem={saleItem}
                  canHide={canHide}
                  onConfirm={(s) => {
                    saleItem.setTitle(s);
                    updateItem.callback({ title: s });
                  }}
                />
              </Box>
              <Box
                className="font-mono"
                textAlign="right"
                justifyContent="flex-end"
              >
                <EditableText
                  size={9}
                  type="number"
                  readOnly={!canChangeQuantity}
                  display="inline-flex"
                  defaultValue={1}
                  value={saleItem.quantity}
                  onConfirm={(s, n) => {
                    if (n !== undefined && !isNaN(n)) {
                      saleItem.setQuantity(n);
                      updateItem.callback({
                        quantity: n,
                      });
                    }
                  }}
                  maximumFractionDigits={4}
                  minimumFractionDigits={0}
                  minWidth={'80px'}
                  unit={formatUnit(intl, saleItem.unit || Unit.PCS)}
                />
              </Box>
              <Box
                className="font-mono"
                textAlign="right"
                justifyContent="flex-end"
              >
                {saleItem.is_hidden ? (
                  '–'
                ) : (
                  <EditableText
                    size={13}
                    type="number"
                    display="inline-flex"
                    readOnly={!canChangeUnitPrice}
                    defaultValue={0}
                    value={saleItem.unit_price}
                    onConfirm={(s, n) => {
                      if (n !== undefined && !isNaN(n)) {
                        saleItem.setUnitPrice(n);
                        updateItem.callback({
                          unit_price: n,
                        });
                      }
                    }}
                    unit={saleItem.unit_price_currency}
                    minWidth="140px"
                  />
                )}
              </Box>
              <Box
                className="font-mono"
                textAlign="right"
                justifyContent="flex-end"
              >
                {saleItem.is_hidden ? (
                  '–'
                ) : (
                  <EditableText
                    size={5}
                    type="number"
                    display="inline-flex"
                    readOnly={!canChangeUnitPrice}
                    defaultValue={0}
                    value={(saleItem.discount || 0) * 100}
                    onConfirm={(s, n) => {
                      if (n !== undefined && !isNaN(n)) {
                        saleItem.setDiscount(n / 100);
                        updateItem.callback({
                          discount: n / 100,
                        });
                      }
                    }}
                    unit={'%'}
                    minimumFractionDigits={0}
                    maximumFractionDigits={2}
                  />
                )}
              </Box>
              <Box
                className="font-mono"
                textAlign="right"
                justifyContent="flex-end"
              >
                {saleItem.is_hidden ? (
                  '–'
                ) : (
                  <EditableText
                    size={13}
                    type="number"
                    display="inline-flex"
                    defaultValue={0}
                    readOnly={!canChangeUnitPrice}
                    value={saleItem.computedPrice}
                    onConfirm={(s, n) => {
                      if (n !== undefined && !isNaN(n)) {
                        saleItem.setPrice(n);
                        updateItem.callback({
                          unit_price: saleItem.unit_price,
                        });
                      }
                    }}
                    unit={saleItem.unit_price_currency}
                    minWidth="140px"
                  />
                )}
              </Box>
              <Box
                className={classNames('font-mono', styles.slideInLastChild)}
                textAlign="right"
                justifyContent="flex-end"
              >
                {(saleItem.product !== null ||
                  (saleItem.child_items && saleItem.child_items.length > 0)) &&
                saleItem.own_cost_price === null ? (
                  // If it has a product and has children, we know to "mute" the cost price.
                  <>
                    <CostPriceText costPrice={saleItem.cost_price} vertical />
                    <Button
                      small
                      iconLeft="edit"
                      minimal
                      onClick={() => {
                        saleItem.setCostPrice(
                          saleItem.cost_price.precise ||
                            saleItem.cost_price.minimum ||
                            0
                        );
                        updateItem.callback({
                          own_cost_price: saleItem.own_cost_price,
                        });
                      }}
                    />
                  </>
                ) : (
                  <>
                    <EditableText
                      size={13}
                      type="number"
                      display="inline-flex"
                      defaultValue={0}
                      value={
                        (saleItem.own_cost_price || 0) * saleItem.quantity!
                      }
                      unit={
                        saleItem.own_cost_price_currency ||
                        saleItem.unit_price_currency
                      }
                      onConfirm={(s, n) => {
                        if (n !== undefined && !isNaN(n)) {
                          saleItem.setCostPrice(n);
                          updateItem.callback({
                            own_cost_price: saleItem.own_cost_price,
                          });
                        }
                      }}
                    />
                    {(saleItem.product !== null ||
                      (saleItem.child_items &&
                        saleItem.child_items.length > 0)) && (
                      <Button
                        small
                        iconLeft="calculator"
                        minimal
                        onClick={() => {
                          saleItem.setCostPrice(null);
                          updateItem.callback({
                            own_cost_price: null,
                          });
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
              <Box
                className="font-mono"
                textAlign="right"
                justifyContent="flex-end"
              >
                {saleItem.is_hidden ? (
                  '–'
                ) : (
                  <>
                    <ContributionMarginText
                      contributionMargin={saleItem.contribution_margin}
                      showIndication
                      vertical
                    />{' '}
                    <span className="muted">
                      (
                      {saleItem.contribution_margin_ratio.precise === null &&
                        '>'}
                      <FormattedNumber
                        style="percent"
                        value={
                          saleItem.contribution_margin_ratio.precise ||
                          saleItem.contribution_margin_ratio.minimum ||
                          0
                        }
                      />
                      )
                    </span>
                  </>
                )}
              </Box>
              <Box textAlign="center" justifyContent="center">
                <Checkbox
                  disabled={!canHide}
                  flexGrow={0}
                  flexShrink={0}
                  display="inline-flex"
                  checked={saleItem.is_hidden}
                  onChange={(flag: boolean) => {
                    saleItem.setHidden(flag);
                    updateItem.callback({
                      is_hidden: flag,
                    });
                  }}
                />
              </Box>
              {showStatus && (
                <Box>
                  <SaleItemStatusCell saleItem={saleItem} />
                </Box>
              )}

              <Box>
                <SaleItemActionMenu
                  saleItem={saleItem}
                  onDeleteLine={() => {
                    if (
                      saleItem.task ||
                      saleItem.task === undefined ||
                      saleItem.related_purchase_orders.length > 0
                    ) {
                      deleteLineAlert.show(async () => onDeleteLine());
                    } else {
                      onDeleteLine();
                    }
                  }}
                  canChangeUnitPrice={canChangeUnitPrice}
                  canAddItems={canAddItems}
                  showStatus={showStatus}
                />
              </Box>
            </>
          )}
        </Box>
        {saleItem.child_items &&
          saleItem.child_items.map((child: ISaleItem | undefined, i) =>
            child ? (
              <Box
                key={i}
                className={styles.row}
                display="grid"
                gridAutoFlow="column"
                alignItems="center"
                gridTemplateColumns={gridTemplateColumns}
                backgroundColor="white"
              >
                <Box position="sticky" left={0} zIndex={2} />

                <Box
                  position="sticky"
                  zIndex={2}
                  left={canChangePosition ? '24px' : 0}
                >
                  <Box display="flex" alignItems="center" width="100%">
                    <Icon
                      icon="arrow-right"
                      marginRight={majorScale(1)}
                      marginLeft={majorScale(4)}
                    />
                    <EditableLineTitle
                      saleItem={child}
                      canHide={canHide}
                      onConfirm={(s) => {
                        child.setTitle(s);
                        saleItemStore.updateItem(child as any, { title: s });
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  textAlign="right"
                  justifyContent="flex-end"
                  className="font-mono"
                >
                  <EditableText
                    type="number"
                    size={8}
                    display="inline-flex"
                    readOnly={!canChangeQuantity}
                    defaultValue={1}
                    value={child.quantity}
                    maximumFractionDigits={4}
                    minimumFractionDigits={0}
                    unit={formatUnit(
                      intl,
                      child.product ? child.product.unit : Unit.PCS
                    )}
                    maxWidth="100px"
                    onConfirm={(s, n) => {
                      if (n !== undefined && !isNaN(n)) {
                        child.setQuantity(n);
                        // Update the child and refresh the parent
                        const snapshot = getSnapshot(child);
                        saleItemStore.updateItem(child, snapshot).then(() => {
                          saleItemStore.getItem(saleItem.id, true);
                          if (snapshot.sale)
                            saleStore.getItem(snapshot.sale, true);
                          if (snapshot.agreement)
                            agreementStore.getItem(snapshot.agreement, true);
                        });
                      }
                    }}
                  />
                </Box>
                <Box
                  textAlign="right"
                  justifyContent="flex-end"
                  className="font-mono"
                >
                  -
                </Box>
                <Box
                  textAlign="right"
                  justifyContent="flex-end"
                  className="font-mono"
                >
                  -
                </Box>
                <Box
                  textAlign="right"
                  justifyContent="flex-end"
                  className="font-mono"
                >
                  -
                </Box>
                <Box
                  textAlign="right"
                  justifyContent="flex-end"
                  className="font-mono"
                >
                  <CostPriceText costPrice={child.cost_price} vertical />
                </Box>
                <Box
                  textAlign="right"
                  justifyContent="flex-end"
                  className="font-mono"
                >
                  -
                </Box>
                <Box textAlign="center" justifyContent="center">
                  <Checkbox
                    disabled={!canHide}
                    flexGrow={0}
                    flexShrink={0}
                    display="inline-flex"
                    checked={child.is_hidden}
                    onChange={(flag: boolean) => {
                      child.setHidden(flag);
                      saleItemStore.updateItem(child, {
                        is_hidden: flag,
                      });
                    }}
                  />
                </Box>
                {showStatus && (
                  <Box>
                    <SaleItemStatusCell saleItem={child} />
                  </Box>
                )}

                <Box>
                  <SaleItemActionMenu
                    saleItem={child}
                    onDeleteLine={async () => {
                      if (
                        child.related_purchase_orders.length > 0 ||
                        child.task ||
                        child.task === undefined
                      ) {
                        deleteLineAlert.show(async () =>
                          saleItemStore
                            .deleteItem(child)
                            .catch(toaster.showDefaultError)
                        );
                      } else {
                        saleItemStore
                          .deleteItem(child)
                          .catch(toaster.showDefaultError);
                      }
                    }}
                    canChangeUnitPrice={canChangeUnitPrice}
                    canAddItems={canAddItems}
                    showStatus={showStatus}
                  />
                </Box>
              </Box>
            ) : (
              <Box key={`index-${i}`} style={{ height: '58px' }}>
                <Box>
                  <Spinner size={16} />
                </Box>
                <Box />
              </Box>
            )
          )}
      </Box>
    );
  }
);

const EditableLineTitle = memo(
  observer(
    ({
      saleItem,
      onConfirm,
      canHide,
    }: {
      saleItem: ISaleItem;
      onConfirm: (v: string, n?: number | undefined) => void;
      canHide: Boolean;
    }) => {
      return (
        <Box display="flex" alignItems="center" width="100%">
          {(saleItem.product || saleItem.product === undefined) &&
            (!saleItem.product ? (
              <Spinner size={16} />
            ) : (
              <Box
                display="flex"
                alignItems="center"
                flexDirection="column"
                marginRight={minorScale(1)}
                minWidth="60px"
                flexShrink={0}
              >
                <ProductThumbnail
                  product={saleItem.product}
                  showName={false}
                  showSKU={true}
                  small
                  stacked
                />
              </Box>
            ))}
          <EditableText
            className="p small"
            display="inline-flex"
            readOnly={!canHide}
            value={saleItem.title}
            placeholder="Skriv en titel"
            maxLength={600}
            onConfirm={onConfirm}
            minWidth="140px"
            maxWidth="100%"
            width="100%"
            autoHeight
          />
        </Box>
      );
    }
  )
);
